import React, { useState, useEffect } from "react";
import moment from "moment";

type CouponDiscountType = "price" | "percentage";

type CouponCategoryRef = string | number | { id: string | number };

type CouponData = {
  title: string;
  expire_date: string;
  shopping_categories: CouponCategoryRef[] | null;
  min_purchase: number;
  discount_type: CouponDiscountType;
  discount_amount: number;
  discount_rate: number;
  max_discount: number;
};

type Coupon = {
  id: string | number;
  coupon: CouponData;
};

type TargetItem = {
  product_id: string | number;
  option_id: string | number;
  category: string;
  price: number;
  quantity: number;
};

type SelectedCoupon = {
  product_id: string | number;
  option_id: string | number;
  coupon_id: string | number;
};

type ToggleShoppingDiscountCouponSelection = (
  productId: TargetItem["product_id"],
  couponId: Coupon["id"],
  optionId: TargetItem["option_id"],
  discountAmount: number,
  discountType: CouponDiscountType,
  minPurchase: number,
  discountRate: number,
  maxDiscount: number
) => void;

interface CouponShoppingItemProps {
  coupon: Coupon;
  targetItem: TargetItem;
  selectCoupon: SelectedCoupon[];
  toggleShoppingDiscountCouponSelection: ToggleShoppingDiscountCouponSelection;
}

const CouponShoppingItem: React.FC<CouponShoppingItemProps> = ({
  coupon,
  targetItem,
  selectCoupon,
  toggleShoppingDiscountCouponSelection,
}) => {
  const [disabled, setDisabled] = useState<boolean>(false);
  const [disabledReason, setDisabledReason] = useState<string>("");
  const [isChecked, setIsChecked] = useState<boolean>(false);

  useEffect(() => {
    // Check if coupon is expired or invalid
    const is_expired = new Date(coupon.coupon.expire_date) < new Date();
    // 백엔드는 shopping_categories를 [{id, name, subdomain}, ...] 객체 배열로 내려줌. id만 꺼내 타입 무시하고 비교.
    const is_valid_category = (coupon.coupon.shopping_categories ?? []).some((categoryRef) => {
      const categoryId = typeof categoryRef === "object" && categoryRef !== null ? categoryRef.id : categoryRef;
      return String(categoryId) === String(targetItem.category);
    });
    const is_max_purchase = coupon.coupon.min_purchase <= targetItem.price * targetItem.quantity;
    const is_vaild_discount =
      coupon.coupon.discount_type === "price"
        ? targetItem.price * targetItem.quantity - coupon.coupon.discount_amount >= 0
        : targetItem.price * targetItem.quantity * (1 - coupon.coupon.discount_rate / 100) >= 0;

    const isSelectedCoupon = selectCoupon.find(
      (coupons) => coupons.product_id === targetItem.product_id && coupons.option_id !== targetItem.option_id && coupons.coupon_id === coupon.id
    );

    setDisabled(is_expired || !is_valid_category || !is_max_purchase || !is_vaild_discount || !!isSelectedCoupon);
    setDisabledReason(
      is_expired
        ? "유효기간이 만료된 쿠폰입니다"
        : !is_valid_category
        ? "이 상품에는 적용할 수 없는 쿠폰입니다"
        : !is_max_purchase
        ? `${coupon.coupon.min_purchase.toLocaleString()}원 이상 구매 시 사용 가능`
        : isSelectedCoupon
        ? "다른 옵션에 이미 적용된 쿠폰입니다"
        : !is_vaild_discount
        ? "할인 적용이 불가한 쿠폰입니다"
        : ""
    );

    // Check if current coupon is selected
    setIsChecked(
      selectCoupon?.length > 0
        ? !!selectCoupon.find(
          (coupons) => coupons.coupon_id === coupon.id && coupons.product_id === targetItem.product_id && coupons.option_id === targetItem.option_id
        )
        : false
    );
  }, [coupon, targetItem, selectCoupon]);

  const handleCouponSelection = () => {
    toggleShoppingDiscountCouponSelection(
      targetItem.product_id,
      coupon.id,
      targetItem.option_id,
      coupon.coupon.discount_amount,
      coupon.coupon.discount_type,
      coupon.coupon.min_purchase,
      coupon.coupon.discount_rate,
      coupon.coupon.max_discount
    );
  };

  const formatWon = (value: number) => {
    const num = Number(value || 0);
    if (num >= 10000 && num % 10000 === 0) return `${(num / 10000).toLocaleString()}만원`;
    return `${num.toLocaleString()}원`;
  };
  const periodLabel = coupon.coupon.expire_date
    ? `~${moment(String(coupon.coupon.expire_date).slice(0, 10)).format("YYYY.MM.DD")} 까지`
    : "상시 사용 가능";

  return (
    <li
      onClick={!disabled ? handleCouponSelection : undefined}
      className={[
        "relative flex rounded-xl overflow-hidden border transition-[transform,border-color,box-shadow] duration-300 ease-[cubic-bezier(0.2,0,0,1)] select-none",
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer active:scale-[0.96]",
        isChecked
          ? "border-[#e16036] shadow-[0_4px_14px_rgba(225,96,54,0.18)]"
          : "border-gray-200 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:border-[#f0c4b0]",
      ].join(" ")}
    >
      {/* 티켓 노치 (점선 위치 상·하단 반원 홈) */}
      <span className="absolute left-20 top-0 z-10 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-100" />
      <span className="absolute left-20 bottom-0 z-10 h-3 w-3 -translate-x-1/2 translate-y-1/2 rounded-full bg-gray-100" />
      {/* 왼쪽: 할인 금액/율 강조 */}
      <div className={[
        "w-20 shrink-0 flex flex-col items-center justify-center px-2 py-4 transition-colors duration-300 ease-[cubic-bezier(0.2,0,0,1)]",
        isChecked ? "bg-[#e16036]" : "bg-[#FFF0EA]",
      ].join(" ")}>
        {coupon.coupon.discount_type === "price" ? (
          <>
            <span className={`text-lg font-bold leading-tight tabular-nums transition-colors duration-300 ease-[cubic-bezier(0.2,0,0,1)] ${isChecked ? "text-white" : "text-[#e16036]"}`}>
              {coupon.coupon.discount_amount.toLocaleString()}
            </span>
            <span className={`text-[11px] font-semibold transition-colors duration-300 ease-[cubic-bezier(0.2,0,0,1)] ${isChecked ? "text-orange-100" : "text-[#e16036]"}`}>원 할인</span>
          </>
        ) : (
          <>
            <span className={`text-2xl font-bold leading-tight tabular-nums transition-colors duration-300 ease-[cubic-bezier(0.2,0,0,1)] ${isChecked ? "text-white" : "text-[#e16036]"}`}>
              {coupon.coupon.discount_rate}%
            </span>
            <span className={`text-[11px] font-semibold transition-colors duration-300 ease-[cubic-bezier(0.2,0,0,1)] ${isChecked ? "text-orange-100" : "text-[#e16036]"}`}>할인</span>
          </>
        )}
      </div>

      {/* 점선 구분 */}
      <div className="border-l-2 border-dashed border-gray-200 self-stretch" />

      {/* 오른쪽: 쿠폰 정보 */}
      <div className="flex-1 px-4 py-3.5 flex items-center justify-between gap-2 min-w-0">
        <div className="min-w-0">
          <p className="m-0 mb-1 line-clamp-2 text-sm font-bold text-gray-900" style={{ fontFamily: "Gmarket-B" }}>
            {coupon.coupon.title}
          </p>
          {disabled ? (
            <p className="text-xs font-medium text-red-600">{disabledReason}</p>
          ) : (
            <>
              <p className="text-xs font-semibold text-[#e16036] mb-0.5">{periodLabel}</p>
              {(coupon.coupon.min_purchase > 0 ||
                (coupon.coupon.discount_type === "percentage" && coupon.coupon.max_discount > 0)) && (
                <p className="text-xs text-gray-700">
                  {coupon.coupon.min_purchase > 0 && `${coupon.coupon.min_purchase.toLocaleString()}원 이상 결제 시`}
                  {coupon.coupon.discount_type === "percentage" &&
                    coupon.coupon.max_discount > 0 &&
                    ` (최대 ${formatWon(coupon.coupon.max_discount)})`}
                </p>
              )}
            </>
          )}
        </div>

        {!disabled && (
          <div className={[
            "shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors duration-300 ease-[cubic-bezier(0.2,0,0,1)]",
            isChecked ? "bg-[#e16036] border-[#e16036]" : "border-gray-300",
          ].join(" ")}>
            <svg
              className={[
                "w-3 h-3 text-white transition-[opacity,scale,filter] duration-300 ease-[cubic-bezier(0.2,0,0,1)]",
                isChecked ? "opacity-100 scale-100 blur-0 delay-75" : "opacity-0 scale-[0.25] blur-[4px]",
              ].join(" ")}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
      </div>
    </li>
  );
};

export default React.memo(CouponShoppingItem);
